import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { ApiService } from 'src/app/core/services';
import {
  ActivateLineParams,
  CreateLineParams,
  CreateLinesParams,
  CreateLinesResponse,
  GetEndUserLinesParams,
  GetEndUserLinesResponse,
  GetEndUsersByLinesParams,
  GetEndUsersByLinesResponse,
  GetFilteredLinesParams,
  GetFilteredLinesResponse,
  GetLineByIccidParams,
  GetLinesSummaryCsvParams,
  GetLinesSummaryCsvResponse,
  GetLinesSummaryParams,
  GetLinesSummaryResponse,
  GetLineStatusHistoryParams,
  GetLineStatusHistoryResponse,
  LineSimSwapParams,
  LineSimSwapResponse,
  UpdateLineParams,
  UpdateLinesParams,
  UpdateLinesResponse,
  UpdateLinesStatusParams,
  UpdateLinesStatusResponse,
} from 'src/app/core/proto/whitepages/lines/dtos';
import { DeactivateLinesParams } from '../dtos/deactivate-lines.params';
import { DeactivateLinesResponse } from '../dtos/deactivate-lines.response';
import { Line } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class LinesService {
  constructor(private apiService: ApiService) {}

  getFilteredLines(
    getFilteredLinesParams: GetFilteredLinesParams
  ): Observable<GetFilteredLinesResponse> {
    let params: HttpParams = this.apiService.queryParams(
      getFilteredLinesParams
    );
    return this.apiService.get('/v2/lines', params);
  }

  createLines(
    createLinesParams: CreateLinesParams
  ): Observable<CreateLinesResponse> {
    return this.apiService.post('/v2/lines', createLinesParams);
  }

  createLine(createLineParams: CreateLineParams): Observable<Line> {
    return this.apiService.post('/v2/lines/line', createLineParams);
  }

  updateLines(
    updateLinesParams: UpdateLinesParams
  ): Observable<UpdateLinesResponse> {
    return this.apiService.put('/v2/lines', updateLinesParams);
  }

  updateLine(updateLineParams: UpdateLineParams): Observable<Line> {
    return this.apiService.put(
      `/v2/lines/${updateLineParams.lineId}`,
      updateLineParams
    );
  }

  activateLine(activateLineParams: ActivateLineParams): Observable<Line> {
    return this.apiService.put(
      `/v2/lines/${activateLineParams.lineId}/activate`,
      activateLineParams
    );
  }

  deactivateLines(
    deactivateLinesParams: DeactivateLinesParams
  ): Observable<DeactivateLinesResponse> {
    const params: HttpParams = this.apiService.queryParams(
      deactivateLinesParams
    );

    return this.apiService.delete('/v2/lines', params);
  }

  simSwap(
    lineId: string,
    lineSimSwapParams: LineSimSwapParams
  ): Observable<LineSimSwapResponse> {
    return this.apiService.post(
      `/v2/lines/${lineId}/sim-swap`,
      lineSimSwapParams
    );
  }

  updateLinesStatus(
    UpdateLinesStatusParams: UpdateLinesStatusParams
  ): Observable<UpdateLinesStatusResponse> {
    return this.apiService.put('/v2/lines/status', UpdateLinesStatusParams);
  }

  getLinesSummary(
    getLinesSummaryParams: GetLinesSummaryParams
  ): Observable<GetLinesSummaryResponse> {
    return this.apiService.post(
      `/v2-internal/lines-summary`,
      getLinesSummaryParams
    );
  }

  getLinesSummaryCsv(
    getLinesSummaryCsvParams: GetLinesSummaryCsvParams
  ): Observable<GetLinesSummaryCsvResponse> {
    let formData = new FormData();
    Object.keys(getLinesSummaryCsvParams).forEach((key) => {
      if (getLinesSummaryCsvParams[key]) {
        formData.append(key, getLinesSummaryCsvParams[key]);
      }
    });
    return this.apiService.postMultipart(
      `/v2-internal/lines-summary-csv`,
      formData
    );
  }

  getEndUserLines(
    params: GetEndUserLinesParams
  ): Observable<GetEndUserLinesResponse> {
    const queryParams = this.apiService.queryParams(params);
    return this.apiService.get(
      `/v2/end-users/${params.endUserId}/lines`,
      queryParams
    );
  }

  getLineByIccid(params: GetLineByIccidParams): Observable<Line> {
    const queryParams = this.apiService.queryParams(params);
    return this.apiService.get(`/v2/sims/${params.iccid}/line`, queryParams);
  }

  getLineStatusHistory(
    params: GetLineStatusHistoryParams
  ): Observable<GetLineStatusHistoryResponse> {
    return this.apiService.post(`/v2/lines/status/history`, params);
  }

  getEndUsersByLines(
    params: GetEndUsersByLinesParams
  ): Observable<GetEndUsersByLinesResponse> {
    const queryParams = this.apiService.queryParams(params);
    return this.apiService.get(`/v2-internal/lines/end-users`, queryParams);
  }
}
